/**
 * ErrorBoundary Component
 * Catches React errors and displays a fallback UI
 */
import React from 'react';
import { Button, Result } from 'antd';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });

    // Log to error tracking service if available
    if (window.errorTracker) {
      window.errorTracker.captureException(error, { errorInfo });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
          <Result
            status="error"
            title="Something went wrong"
            subTitle={
              isDevelopment
                ? this.state.error?.message || 'An unexpected error occurred'
                : 'We encountered an unexpected error. Please try again.'
            }
            extra={[
              <Button type="primary" key="reset" onClick={this.handleReset}>
                Try Again
              </Button>,
              <Button key="reload" onClick={this.handleReload}>
                Reload Page
              </Button>
            ]}
          />

          {isDevelopment && this.state.errorInfo && (
            <details style={{ marginTop: '24px', whiteSpace: 'pre-wrap' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>
                Error Details (Development Only)
              </summary>
              <div style={{
                backgroundColor: '#f5f5f5',
                padding: '16px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                overflow: 'auto'
              }}>
                <div style={{ marginBottom: '16px' }}>
                  <strong>Error:</strong>
                  <pre style={{ margin: '8px 0' }}>{this.state.error?.toString()}</pre>
                </div>
                <div>
                  <strong>Component Stack:</strong>
                  <pre style={{ margin: '8px 0' }}>{this.state.errorInfo.componentStack}</pre>
                </div>
              </div>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
